import { MONTH_DAY_YEAR_HOUR_MINUTE_DATE_FORMAT } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { LOGIN_HINT_EXPIRATION_TYPE_OPTIONS } from "@constants/login-hint-expiration-type-options";
import { OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID } from "@constants/oidc-login-hint";
import { EXCLUDED_ATTRIBUTE_ADDRESS_FIELDS_IDS, TELEPHONE_ATTRIBUTE_TYPE_FIELD_IDs, TELEPHONE_ATTRIBUTE_TYPE_ID } from "@constants/self-service-constants";
import { DateInputFormGroup } from "@containers/common/date-input-form-group/date-input-form-group";
import { InternationalTelephoneFormGroup } from "@containers/common/international-telephone-form-group/international-telephone-form-group";
import { LanguageSelectorContainer } from "@containers/common/language-selector-container/language-selector-container";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { SelfServicePrefilledAttributes } from "@containers/common/self-service-prefilled-attributes/self-service-prefilled-attributes";
import { useModalContext } from "@context/use-modal-context";
import { ButtonTypes } from "@enums/button-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LanguageSelectorContainerTypes } from "@enums/language-selector-container-types";
import { LayoutTypes } from "@enums/layout-types";
import { LoginHintExpirationTypeTypes } from "@enums/login-hint-expiration-type-types";
import { ModalTypes } from "@enums/modal-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { partiallyFormatRpServiceConfiguration } from "@formatters/rp-services/rp-service-configuration-partial-formatter";
import { DEFAULT_AXNUI_CONTENT, DEFAULT_AXNUI_LAYOUT } from "@iddataweb/idw-common-ui/constants";
import { AttributeTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContentCustomAttributeField, MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { SelectOption } from "@idw-types/common";
import { LoginHintExpirationInfo } from "@idw-types/common/login-hint-info";
import { FormattedMessageTemplate } from "@idw-types/data/message-template";
import {
    AxnAttributeTypeField,
    AxnAttributeTypeFieldCustomization,
    AxnAvailablePropertyCategory,
    AxnCompany,
    AxnCountry,
    AxnIdentityProvider
} from "@idw-types/data/reference-data/axn";
import { AttributeClassification, AttributeLabel, AttributeType, RpServiceAttributeProperty, RpServiceAttributePropertyMap, SelfServiceVerificationAttribute } from "@idw-types/data/reference-data/generic";
import {
    FormattedRpServiceConfigurationAttributeWithFields,
    PartiallyFormattedRpServiceConfiguration,
    RpServiceConfigurationRedirect
} from "@idw-types/data/rp-service-configuration";
import {
    getRpServiceConfigurationRequest,
    getVerificationUrlRequest,
    sendVerificationMessageRequest
} from "@requests/service-configuration";
import { logAndDisplayErrorModal } from "@utils/app-alert-util";
import { getCustomAttributeCustomJSON } from "@utils/attribute-input-util";
import { getCustomLanguages } from "@utils/language-selector-util";
import { getLoginHintExpirationInfo, loginHintExpirationValueToMs } from "@utils/login-hint/login-hint-utils";
import { getCombinedAppliedProfileRedirectURLs, getRpServiceConfigurationPropertyRedirectURLs } from "@utils/redirect-urls/redirect-urls-util";
import { getAllAttributeTypeFieldsValidationErrors } from "@utils/self-service/prefilled-attribute-validation-util";
import { getLoginHintExpirationValueValidationErrors } from "@utils/validation/shared-validation-util";
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { ParsedCountry } from "react-international-phone";
import 'react-international-phone/style.css';
import { useDispatch } from "react-redux";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import { LoadingIndicator } from "../../common/loading-indicator/loading-indicator";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import defaultStyles from "../../common/modals/default-modal/default-modal.less";
import {
    CountrySelectFormGroup,
    LoginIdpSelectFormGroup,
    MessageTemplateSelectFormGroup,
    RedirectToSelectFormGroup
} from "../../common/select-form-groups";
import { VerificationLinkProviderResultModal } from "../verification-link-provider-result-modal/verification-link-provider-result-modal";
import styles from "./verification-link-provider-modal.less";
import { MessageTemplateTypes } from "@enums/message-template-types";

type VerificationLinkProviderModalProps = {
    modalType: ModalTypes;
    resultModalType: ModalTypes;
    idPrefix: string;
    title: string;
    description: string;
    buttonLabel: string;
    onModalClose: () => void;
    rpServiceId: string;
    canModifySelfServiceVerificationExpiration: boolean;
    axnCompanies: Array<AxnCompany>;
    axnCountries: Array<AxnCountry>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    messageTemplates: Array<FormattedMessageTemplate>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    selfServiceVerificationAttributes: Array<SelfServiceVerificationAttribute>;
    attributeTypes: Array<AttributeType>;
    attributeLabels: Array<AttributeLabel>;
    attributeClassifications: Array<AttributeClassification>;
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>;
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>;
};

type VerificationLinkProviderModalState = {
    idps: Array<AxnIdentityProvider>;
    redirects: Array<RpServiceConfigurationRedirect>;
    recipient: string;
    selectedMessageTemplateOption: SelectOption;
    selectedIdpOption: SelectOption;
    selectedDeliveryMethodOption: SelectOption;
    selectedCountryOption: SelectOption;
    selectedRedirectToOption: SelectOption;
    credential: string;
    appId: string;
    asi: string;
    selectedLanguage: LocaleTypes;
    attributeTypeToFieldValueMap: {
        [id: string]: {
            [fieldId: string]: string
        }
    };
    loginHintExpirationType: string;
    loginHintExpirationValue: string;
    loginHintExpirationDate: Date;
    loginHintDuration: string;
};

export const VerificationLinkProviderModal = (props: VerificationLinkProviderModalProps) => {
    const dispatch = useDispatch();
    const modalContext = useModalContext();
    const {
        modalType,
        resultModalType,
        idPrefix,
        title,
        description,
        buttonLabel,
        onModalClose,
        rpServiceId,
        canModifySelfServiceVerificationExpiration,
        axnCompanies,
        axnCountries,
        axnIdentityProviders,
        axnAvailablePropertyCategories,
        messageTemplates,
        selfServiceVerificationAttributes,
        axnAttributeTypeFields,
        axnAttributeTypeFieldCustomizations,
        attributeClassifications,
        attributeLabels,
        attributeTypes,
        rpServiceAttributeProperties,
        rpServiceAttributePropertyMaps
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.SMALL);
    const [isLoadingRpServiceConfiguration, setIsLoadingRpServiceConfiguration] = useState(false);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [loginHintExpirationInfo, setLoginHintExpirationInfo] = useState<LoginHintExpirationInfo>(null);
    const [rpServiceConfiguration, setRpServiceConfiguration] = useState<PartiallyFormattedRpServiceConfiguration>(null);

    const [state, setState] = useState<VerificationLinkProviderModalState>({
        idps: [],
        redirects: [],
        recipient: "",
        selectedLanguage: null,
        selectedMessageTemplateOption: null,
        selectedIdpOption: null,
        selectedCountryOption: null,
        selectedRedirectToOption: null,
        selectedDeliveryMethodOption: null,
        credential: "",
        appId: "",
        asi: "",
        attributeTypeToFieldValueMap: {},
        loginHintExpirationType: null,
        loginHintExpirationValue: null,
        loginHintDuration: null,
        loginHintExpirationDate: null
    });

    const getRpServiceConfiguration = async () => {
        setIsLoadingRpServiceConfiguration(true);
        const { data, error } = await getRpServiceConfigurationRequest(rpServiceId);
        if (data) {
            const formattedRpServiceConfiguration = partiallyFormatRpServiceConfiguration(
                data,
                axnIdentityProviders,
                axnCompanies,
                axnAvailablePropertyCategories,
                attributeTypes,
                attributeLabels,
                attributeClassifications,
                rpServiceAttributeProperties,
                rpServiceAttributePropertyMaps
            );
            setRpServiceConfiguration(formattedRpServiceConfiguration);
            setIsLoadingRpServiceConfiguration(false);
        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.getRpServiceConfiguration.error.title,
                resources.requests.getRpServiceConfiguration.error.message
            );
            onModalClose();
        }
    };

    useEffect(() => {
        if (!isLoadingRpServiceConfiguration && rpServiceId) {
            getRpServiceConfiguration();
        }
    }, [rpServiceId]);
    const getDefaultMessageTemplate = (deliveryMethod: string): SelectOption => {
        if (deliveryMethod === MessageTemplateTypes.EMAIL) {
            const defaultEmailTemplate = messageTemplates.find(template => template.id === "1");
            if (defaultEmailTemplate) {
                return { label: defaultEmailTemplate.name, value: defaultEmailTemplate.id };
            }
        } else if (deliveryMethod === MessageTemplateTypes.SMS) {
            const defaultSmsTemplate = messageTemplates.find(template => template.id === "50");
            if (defaultSmsTemplate) {
                return { label: defaultSmsTemplate.name, value: defaultSmsTemplate.id };
            }
        }
        return null;
    };

    const setInitialState = () => {
        let defaultCountry: SelectOption = null;
        if (axnCountries.length > 0) {
            const unitedStates = axnCountries.find(country => country.id === "236");
            if (unitedStates) {
                defaultCountry = { label: unitedStates.name, value: unitedStates.alpha2 };
            } else {
                defaultCountry = { label: axnCountries[0].name, value: axnCountries[0].alpha2 };
            }
        }


        const redirects: Array<RpServiceConfigurationRedirect> = [];

        if (rpServiceConfiguration.redirects && rpServiceConfiguration.redirects.length > 0) {
            rpServiceConfiguration.redirects.forEach(sr => {
                redirects.push({ id: sr.id, redirectURI: sr.redirectURI, rpServiceID: sr.rpServiceID });
            });
        } else if (rpServiceConfiguration.callBackURL) {
            redirects.push({ id: null, redirectURI: rpServiceConfiguration.callBackURL, rpServiceID: rpServiceId.toString() });
        }

        const redirectPropertyURLs = getRpServiceConfigurationPropertyRedirectURLs(rpServiceConfiguration.properties, rpServiceConfiguration.id, false);
        redirectPropertyURLs.forEach(rp => {
            redirects.push({ id: rp.id, redirectURI: rp.url, rpServiceID: rp.rpServiceID });
        });

        const profileRedirects = getCombinedAppliedProfileRedirectURLs(rpServiceConfiguration.appliedProfiles, false);
        profileRedirects.forEach(pr => {
            redirects.push({
                id: null,
                redirectURI: pr.url,
                rpServiceID: rpServiceConfiguration.id
            });
        });


        const loginHintExpirationInfo = getLoginHintExpirationInfo(rpServiceConfiguration.formattedProperties, rpServiceConfiguration.appliedProfiles);
        setLoginHintExpirationInfo(loginHintExpirationInfo);

        const { loginHintExpirationType, loginHintDuration, loginHintExpirationDate, loginHintExpirationValue } = loginHintExpirationInfo;

        setState({
            idps: rpServiceConfiguration.idps || [],
            redirects: redirects,
            recipient: "",
            selectedLanguage: LocaleTypes.ENGLISH,
            selectedMessageTemplateOption: null,
            selectedDeliveryMethodOption: null,
            selectedIdpOption: rpServiceConfiguration.idps?.length ? {
                label: rpServiceConfiguration.idps[0].name,
                value: rpServiceConfiguration.idps[0].code
            } : null,
            selectedCountryOption: defaultCountry,
            selectedRedirectToOption: redirects.length ? {
                label: redirects[0].redirectURI,
                value: redirects[0].redirectURI
            } : null,
            credential: "",
            appId: "",
            asi: "",
            attributeTypeToFieldValueMap: {},
            loginHintExpirationType: loginHintExpirationType,
            loginHintExpirationValue: loginHintExpirationValue?.value || null,
            loginHintDuration,
            loginHintExpirationDate
        });
    };

    useEffect(() => {
        if (rpServiceConfiguration) {
            setInitialState();
        }
    }, [rpServiceConfiguration]);

    const resetPII = (onModalClose: () => void) => {
        setInitialState();
        onModalClose();
    };

    const deliveryMethodOptions = [
        { value: 'email', label: 'Email' },
        { value: 'sms', label: 'SMS' }
    ];

    const customLanguages: Array<LocaleTypes> = useMemo(() => {
        const languages = getCustomLanguages(PageLevelTypes.WORKFLOW, rpServiceConfiguration?.appliedProfiles[0]?.profile?.profileProperties);
        return languages || [LocaleTypes.ENGLISH];
    }, [rpServiceConfiguration, state, messageTemplates]);

    const availableTemplates: Array<FormattedMessageTemplate> = useMemo(() => {
        return messageTemplates.filter(template => {
            const languages: Partial<MultiLanguageWrapper<{ [key: string]: any }>> = template.languages;
            const deliveryMethodMatches = !state.selectedDeliveryMethodOption?.value || template.type === state.selectedDeliveryMethodOption?.value;

            return languages && languages[state.selectedLanguage as keyof typeof languages] && deliveryMethodMatches;
        });
    }, [messageTemplates, state.selectedCountryOption, state.selectedIdpOption, state.selectedLanguage, state.selectedDeliveryMethodOption]);


    const renderSecondModal = async (url: string) => {
        if (modalType === ModalTypes.TryNow) {
            window.open(url, "_blank");
        }

        const renderCopyLinkResultModal = (onModalClose: () => void) => {
            return (
                <VerificationLinkProviderResultModal
                    onModalClose={onModalClose}
                    onClickResetPII={resetPII}
                    link={url}
                    modalType={resultModalType}
                    selectedLanguage={state.selectedLanguage}
                    recipient={state.recipient}
                    messageTemplateId={state.selectedMessageTemplateOption?.value}
                    rpServiceId={rpServiceId.toString()}
                    idp={state.selectedIdpOption?.value}
                    country={state.selectedCountryOption?.value}
                    redirectTo={state.selectedRedirectToOption?.value}
                    credential={state.credential}
                    asi={state.asi}
                    appId={state.appId}
                    attributeTypeToFieldValueMap={state.attributeTypeToFieldValueMap}
                    canModifySelfServiceVerificationExpiration={canModifySelfServiceVerificationExpiration}
                    loginHintExpirationType={state.loginHintExpirationType}
                    loginHintExpirationValue={state.loginHintExpirationValue}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.CopyLinkResult,
            renderFunction: renderCopyLinkResultModal
        });
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);

        const attributeTypeToFieldValueMap = { ...state.attributeTypeToFieldValueMap };

        if (
            attributeTypeToFieldValueMap[TELEPHONE_ATTRIBUTE_TYPE_ID]?.[TELEPHONE_ATTRIBUTE_TYPE_FIELD_IDs.dialCode] &&
            !attributeTypeToFieldValueMap[TELEPHONE_ATTRIBUTE_TYPE_ID]?.[TELEPHONE_ATTRIBUTE_TYPE_FIELD_IDs.phone]
        ) {
            delete attributeTypeToFieldValueMap[TELEPHONE_ATTRIBUTE_TYPE_ID];
        }
        if (modalType === ModalTypes.SendMessageWithLink) {
            const { data, error, body } = await sendVerificationMessageRequest(
                state.selectedMessageTemplateOption.value,
                state.selectedLanguage,
                state.recipient,
                rpServiceId.toString(),
                state.selectedIdpOption?.value,
                state.selectedCountryOption?.value,
                state.selectedRedirectToOption?.value,
                state.credential,
                state.asi,
                state.appId,
                attributeTypeToFieldValueMap,
                canModifySelfServiceVerificationExpiration,
                state.loginHintExpirationType,
                state.loginHintExpirationValue
            );
            if (data?.url) {
                renderSecondModal(data.url);
            } else if (error) {
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.requests.getVerificationUrl.error.title,
                    resources.requests.getVerificationUrl.error.message,
                    null,
                    null,
                    body
                );
            }
            setRequestInProgress(false);
        }
        else {
            const { data, error, body } = await getVerificationUrlRequest(
                rpServiceId.toString(),
                state.selectedIdpOption?.value,
                state.selectedCountryOption?.value,
                state.selectedRedirectToOption?.value,
                state.credential,
                state.asi,
                state.appId,
                attributeTypeToFieldValueMap,
                canModifySelfServiceVerificationExpiration,
                state.loginHintExpirationType,
                state.loginHintExpirationValue
            );
            if (data?.url) {
                renderSecondModal(data.url);
            } else if (error) {
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.requests.getVerificationUrl.error.title,
                    resources.requests.getVerificationUrl.error.message,
                    null,
                    null,
                    body
                );
            }
            setRequestInProgress(false);
        }


    };

    const handleUpdateRecipient = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            recipient: event.target.value
        });
    };

    const handleUpdateMessageTemplate = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                selectedMessageTemplateOption: newValue
            });
        }
    };

    const handleUpdateIdp = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                selectedIdpOption: newValue
            });
        }
    };

    const handleUpdateCountry = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                selectedCountryOption: newValue
            });
        }
    };

    const handleUpdateRedirectTo = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                selectedRedirectToOption: newValue
            });
        }
    };

    const handleUpdateCredential = (event: ChangeEvent<HTMLInputElement>) => {
        setState({ ...state, credential: event.target.value });
    };

    const handleUpdateASI = (event: ChangeEvent<HTMLInputElement>) => {
        setState({ ...state, asi: event.target.value });
    };

    const handleUpdateAppId = (event: ChangeEvent<HTMLInputElement>) => {
        setState({ ...state, appId: event.target.value });
    };

    const setSelectedLanguage = (selectedLanguage: LocaleTypes) => {
        setState({
            ...state,
            selectedLanguage: selectedLanguage
        });
    };

    const handleUpdateAttributeField = (partialAttributeTypeToFieldValueMap: { [key: string]: { [key: string]: string } }) => {
        setState({
            ...state,
            attributeTypeToFieldValueMap: {
                ...state.attributeTypeToFieldValueMap,
                ...partialAttributeTypeToFieldValueMap
            }
        });
    };

    const isLoaded = useMemo(() => {
        return !!(!isLoadingRpServiceConfiguration && rpServiceConfiguration && loginHintExpirationInfo);
    }, [isLoadingRpServiceConfiguration, rpServiceConfiguration, loginHintExpirationInfo]);

    const activeAttributeTypesWithFields: Array<FormattedRpServiceConfigurationAttributeWithFields> = useMemo(() => {
        if (rpServiceConfiguration?.loginHintSupported) {
            const attributeTypeToFieldValueMap: { [id: string]: { [id: string]: string } } = {};

            const activeAttributeTypes = rpServiceConfiguration.attributes.filter(attribute =>
                attribute.status === EntityStatusTypes.ACTIVE &&
                (!!selfServiceVerificationAttributes.find(ssva => ssva.attributeTypeID === attribute.attributeType.id) || attribute.templateName === "Custom")
            ).map(attribute => {
                const shouldUseAXNUIComponents = [
                    AttributeTypes.DOB,
                    AttributeTypes.DATE,
                    AttributeTypes.INTERNATIONAL_TELEPHONE
                ].includes(attribute.attributeType.name);
                const attrTypeFields = axnAttributeTypeFields.filter(atf =>
                    atf.attributeTypeID === attribute.attributeType.id &&
                    !EXCLUDED_ATTRIBUTE_ADDRESS_FIELDS_IDS.includes(atf.id)
                );

                const attrTypeFieldsWithCustomizations: Array<AxnAttributeTypeField & { customization: { options: Array<SelectOption>; content: AXNUIContentCustomAttributeField; } }> = [];

                attrTypeFields.forEach(field => {
                    let customAttributeFieldCustomJSON = null;
                    if (attribute.templateName === AttributeTypes.CUSTOM) {
                        customAttributeFieldCustomJSON = getCustomAttributeCustomJSON(
                            attribute,
                            field,
                            DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH],
                            DEFAULT_AXNUI_LAYOUT,
                            axnAttributeTypeFieldCustomizations,
                            LocaleTypes.ENGLISH
                        );
                    }
                    attrTypeFieldsWithCustomizations.push({ ...field, customization: customAttributeFieldCustomJSON });
                });

                return {
                    ...attribute,
                    fields: attrTypeFieldsWithCustomizations,
                    shouldUseAXNUIComponents
                };
            });

            setState({ ...state, attributeTypeToFieldValueMap });

            return activeAttributeTypes;
        }

        return [];
    }, [
        rpServiceConfiguration?.loginHintSupported,
        rpServiceConfiguration?.attributes,
        selfServiceVerificationAttributes,
        axnAttributeTypeFields
    ]);

    const prefilledAttributeValidationErrors = useMemo(() => {
        return getAllAttributeTypeFieldsValidationErrors(state.attributeTypeToFieldValueMap, activeAttributeTypesWithFields);
    }, [state.attributeTypeToFieldValueMap, activeAttributeTypesWithFields]);

    const isAttributesInvalid = useMemo(() => {
        let isInvalid = false;
        Object.keys(prefilledAttributeValidationErrors).forEach(attributeId => {
            if (Object.keys(prefilledAttributeValidationErrors[attributeId]).length > 0) {
                isInvalid = true;
            }
        });
        return isInvalid;
    }, [prefilledAttributeValidationErrors]);

    const hasJwtEncryptedLogin = useMemo(() => {
        if (rpServiceConfiguration?.loginHintSupported) {
            if ((rpServiceConfiguration.properties || []).find(prop => prop.propertyID === OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID && prop.value !== "")) {
                return true;
            } else {
                let hasProfileProp = false;
                (rpServiceConfiguration.appliedProfiles || []).forEach(ap => {
                    if (ap.profile?.profileProperties?.find(prop => prop.availableProperty?.id === OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID && prop.addedToProfile && prop.value !== "")) {
                        hasProfileProp = true;
                    }
                });
                return hasProfileProp;
            }
        }

        return false;
    }, [rpServiceConfiguration]);


    const handleExpirationTypeChange = (selectedOption: SelectOption) => {
        const loginHintExpirationType = selectedOption.value as LoginHintExpirationTypeTypes;
        let value = null;
        let loginHintExpirationDate = null;
        let loginHintExpirationValue = null;

        if (loginHintExpirationType === LoginHintExpirationTypeTypes.DATE_AND_TIME) {
            const date = new Date()
            value = date.getTime().toString();
            loginHintExpirationDate = date;
            loginHintExpirationValue = value;
        }

        setState({
            ...state,
            loginHintExpirationType,
            loginHintExpirationValue,
            loginHintDuration: null,
            loginHintExpirationDate
        });
    };

    const handleExpirationDateTimeChange = (date: Date) => {
        const expirationDate = date?.getTime().toString() || null;
        setState({ ...state, loginHintExpirationValue: expirationDate, loginHintExpirationDate: date, loginHintDuration: null });
    };


    const handleSelectedDeliveryOptionChange = (selectedOption: SelectOption) => {
        const deliveryMethod = selectedOption?.value;
        const defaultMessageTemplate = getDefaultMessageTemplate(deliveryMethod);

        setState({
            ...state,
            selectedDeliveryMethodOption: selectedOption,
            selectedMessageTemplateOption: defaultMessageTemplate,
            recipient: "",
        });
    };
    const handleLoginHintExpirationDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const duration = event.target.value;
        setState({ ...state, loginHintExpirationValue: loginHintExpirationValueToMs(state.loginHintExpirationType, duration), loginHintDuration: duration, loginHintExpirationDate: null });
    }

    const loginHintExpirationTypeSelectedOption = useMemo(() => {
        return LOGIN_HINT_EXPIRATION_TYPE_OPTIONS.find(option => option.value === state.loginHintExpirationType);
    }, [state.loginHintExpirationType]);

    const loginHintResources = resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.loginHint;

    const loginHintValidationErrors = useMemo(() => {
        const { loginHintExpirationType, loginHintExpirationValue } = state;
        return getLoginHintExpirationValueValidationErrors(loginHintExpirationType, loginHintExpirationValue);
    }, [state.loginHintExpirationType, state.loginHintExpirationValue]);


    const [phoneNumber, setPhoneNumber] = useState<{ phone: string; dialCode: string; }>({
        phone: "",
        dialCode: "1"
    });

    const handleTelephoneChange = async (phone: string, meta: { country: ParsedCountry; inputValue: string }) => {
        const dialCode = meta.country?.dialCode || "";
        const phoneNumber = phone.split(`+${dialCode}`)[1] || "";
        const fullPhoneNumber = `+${dialCode}${phoneNumber}`;

        setPhoneNumber({
            phone: phoneNumber,
            dialCode: dialCode
        });

        // Update the state.recipient with the full phone number
        setState({
            ...state,
            recipient: fullPhoneNumber
        });
    };
    const getRecipientInput = () => {
        if (state.selectedDeliveryMethodOption?.value === 'email') {
            return (
                <InputFormGroup
                    controlId="recipient-input"
                    label={resources.modals.verificationLinkProvider.inputs.recipient.label}
                    ariaLabel={resources.modals.verificationLinkProvider.inputs.recipient.label}
                    automationId="recipient-input"
                    placeholder={resources.modals.verificationLinkProvider.inputs.recipient.placeholder.email}
                    value={state.recipient}
                    onChange={handleUpdateRecipient}
                    disabled={requestInProgress}
                    isRequired={true}
                    type="email"
                />
            );
        } else if (state.selectedDeliveryMethodOption?.value === 'sms') {
            return (
                <InternationalTelephoneFormGroup
                    id={`${idPrefix}-recipient-input`}
                    phoneNumber={phoneNumber}
                    isRequired={true}
                    label={resources.modals.verificationLinkProvider.inputs.recipient.label}
                    labelWidth={labelWidth}
                    onChange={handleTelephoneChange}
                    layout={LayoutTypes.ROW}
                    readOnly={requestInProgress}
                />
            );
        }
        return null;
    };

    return (
        <DefaultModal
            automationId="verification-link-provider-modal"
            ariaLabelledBy="verification-link-provider-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            {isLoadingRpServiceConfiguration && (
                <LoadingIndicator title={resources.common.loadingIndicatorText} />
            )}
            {isLoaded && (
                <Form onSubmit={handleSubmit}>
                    <Modal.Header id={"verification-link-provider-title"} closeButton>
                        <Modal.Title>{title}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <span className={defaultStyles.description}>{description}</span>
                        {(modalType === ModalTypes.SendMessageWithLink) && (
                            <>
                                <SelectFormGroup
                                    controlId="delivery-method-input"
                                    label={resources.modals.verificationLinkProvider.inputs.deliveryMethod.label}
                                    ariaLabel={resources.modals.verificationLinkProvider.inputs.deliveryMethod.label}
                                    automationId="delivery-method-input"
                                    selectedOption={state.selectedDeliveryMethodOption}
                                    onChange={handleSelectedDeliveryOptionChange}
                                    disabled={requestInProgress}
                                    isRequired={true}
                                    options={deliveryMethodOptions}
                                    isSearchable={true}
                                    isClearable={false}
                                />
                                {getRecipientInput()}
                                <LanguageSelectorContainer
                                    idPrefix={idPrefix}
                                    availableLanguages={customLanguages}
                                    selectedLanguage={state.selectedLanguage}
                                    languageSelectorContainerType={LanguageSelectorContainerTypes.VERIFICATION_LINK_MODAL}
                                    setSelectedLanguage={setSelectedLanguage}
                                />
                                {state.selectedDeliveryMethodOption?.value && (
                                    <MessageTemplateSelectFormGroup
                                        idPrefix={idPrefix}
                                        onChange={handleUpdateMessageTemplate}
                                        messageTemplates={availableTemplates}
                                        selectedOption={state.selectedMessageTemplateOption}
                                        disabled={requestInProgress}
                                        selectedType={state.selectedDeliveryMethodOption?.value}
                                        isRequired={true}
                                        isSearchable={true}
                                        isClearable={false}
                                        labelWidth={labelWidth}
                                    />
                                )}
                            </>
                        )}
                        {state.idps.length > 0 && (
                            <LoginIdpSelectFormGroup
                                idPrefix={idPrefix}
                                onChange={handleUpdateIdp}
                                axnIdentityProviders={state.idps}
                                selectedOption={state.selectedIdpOption}
                                disabled={requestInProgress}
                                isRequired={true}
                                isSearchable={false}
                                isClearable={false}
                                labelWidth={labelWidth}
                            />
                        )}
                        <CountrySelectFormGroup
                            idPrefix={idPrefix}
                            onChange={handleUpdateCountry}
                            axnCountries={axnCountries}
                            selectedOption={state.selectedCountryOption}
                            disabled={requestInProgress}
                            isRequired={true}
                            isSearchable={true}
                            isClearable={false}
                            labelWidth={labelWidth}
                        />
                        <RedirectToSelectFormGroup
                            idPrefix={idPrefix}
                            onChange={handleUpdateRedirectTo}
                            redirects={state.redirects}
                            selectedOption={state.selectedRedirectToOption}
                            disabled={requestInProgress}
                            isRequired={true}
                            isSearchable={false}
                            isClearable={false}
                            labelWidth={labelWidth}
                        />
                        <InputFormGroup
                            controlId="credential-input"
                            label={resources.modals.verificationLinkProvider.inputs.credential.label}
                            ariaLabel={resources.modals.verificationLinkProvider.inputs.credential.label}
                            automationId="credential-input"
                            placeholder={resources.modals.verificationLinkProvider.inputs.credential.placeholder}
                            value={state.credential}
                            onChange={handleUpdateCredential}
                            disabled={requestInProgress}
                            isRequired={true}
                        />
                        {hasJwtEncryptedLogin && (
                            <InputFormGroup
                                controlId="asi-input"
                                label={resources.modals.verificationLinkProvider.inputs.asi.label}
                                ariaLabel={resources.modals.verificationLinkProvider.inputs.asi.label}
                                automationId="asi-input"
                                placeholder={resources.modals.verificationLinkProvider.inputs.asi.placeholder}
                                value={state.asi}
                                onChange={handleUpdateASI}
                                afterLabelTooltipText={resources.modals.verificationLinkProvider.inputs.asi.tooltip}
                                disabled={requestInProgress}
                                isRequired={false}
                            />
                        )}
                        {rpServiceConfiguration.loginHintSupported && (
                            <InputFormGroup
                                controlId="app-id-input"
                                label={resources.modals.verificationLinkProvider.inputs.appId.label}
                                ariaLabel={resources.modals.verificationLinkProvider.inputs.appId.label}
                                automationId="app-id-input"
                                placeholder={resources.modals.verificationLinkProvider.inputs.appId.placeholder}
                                value={state.appId}
                                onChange={handleUpdateAppId}
                                disabled={requestInProgress}
                                isRequired={false}
                            />
                        )}

                        {(rpServiceConfiguration.loginHintSupported && (loginHintExpirationInfo.isExpireLoginHintEnabled || canModifySelfServiceVerificationExpiration)) && (<div>
                            <SelectFormGroup
                                controlId={"login-hint-expiration-type-select"}
                                automationId={"login-hint-expiration-type-select"}
                                disabled={!canModifySelfServiceVerificationExpiration}
                                label={loginHintResources.inputs.loginHintExpirationType.label}
                                ariaLabel={loginHintResources.inputs.loginHintExpirationType.label}
                                isClearable={false}
                                isSearchable={false}
                                labelWidth={labelWidth}
                                options={LOGIN_HINT_EXPIRATION_TYPE_OPTIONS}
                                selectedOption={loginHintExpirationTypeSelectedOption}
                                isRequired={false}
                                onChange={handleExpirationTypeChange}
                            />

                            {state.loginHintExpirationType === LoginHintExpirationTypeTypes.DATE_AND_TIME && (
                                <>
                                    <DateInputFormGroup
                                        controlId={"login-hint-expiration-date-and-time-date-input"}
                                        automationId={"login-hint-expiration-date-and-time-date-input"}
                                        labelWidth={labelWidth}
                                        label={loginHintResources.inputs.loginHintExpirationDate.label}
                                        ariaLabel={loginHintResources.inputs.loginHintExpirationDate.label}
                                        disabled={!canModifySelfServiceVerificationExpiration}
                                        isRequired={false}
                                        className={styles.datePickerInputContainer}
                                        minDate={new Date()}
                                        maxDate={new Date(2999, 11, 31)}
                                        dateFormat={MONTH_DAY_YEAR_HOUR_MINUTE_DATE_FORMAT}
                                        value={state.loginHintExpirationDate}
                                        onChange={handleExpirationDateTimeChange}
                                        shouldCloseOnSelect={true}
                                        showMonthDropdown={false}
                                        showYearDropdown={true}
                                        showMonthYearPicker={false}
                                        showTimeSelect={true}
                                        isClearable={true}
                                    />
                                    {loginHintValidationErrors.loginHintExpirationValueError}
                                </>)
                            }

                            {state?.loginHintExpirationType === LoginHintExpirationTypeTypes.DURATION && (
                                <>
                                    <InputFormGroup
                                        controlId={"login-hint-expiration-duration-date-input"}
                                        automationId={"login-hint-expiration-duration-date-input"}
                                        label={loginHintResources.inputs.loginHintDuration.label}
                                        ariaLabel={loginHintResources.inputs.loginHintDuration.label}
                                        onChange={handleLoginHintExpirationDurationChange}
                                        value={state.loginHintDuration}
                                        disabled={!canModifySelfServiceVerificationExpiration}
                                        isRequired={false}
                                        labelWidth={labelWidth}
                                    />

                                    {loginHintValidationErrors.loginHintExpirationValueError}
                                </>
                            )
                            }
                        </div>)}

                        {activeAttributeTypesWithFields.length > 0 && (
                            <>
                                <hr />
                                <SelfServicePrefilledAttributes
                                    requestInProgress={requestInProgress}
                                    attributeTypesWithFields={activeAttributeTypesWithFields}
                                    attributeTypeToFieldValueMap={state.attributeTypeToFieldValueMap}
                                    validationErrors={prefilledAttributeValidationErrors}
                                    onAttributeFieldChange={handleUpdateAttributeField}
                                />
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button id={'verification-link-provider-cancel'} variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                        <ButtonWithSpinner
                            id={'verification-link-provider-submit'}
                            disabled={requestInProgress || isAttributesInvalid || loginHintValidationErrors.count > 0}
                            hideSpinner={isAttributesInvalid || loginHintValidationErrors.count > 0}
                            variant="primary"
                            type={ButtonTypes.SUBMIT}
                            label={buttonLabel}
                        />
                    </Modal.Footer>
                </Form>
            )}
        </DefaultModal>
    );
};